import isSignedIn from '../../presenceManager/isSignedIn';
import subscribeForPresenceUpdates from '../services/subscribeForPresenceUpdates';
import unsubscribeFromPresenceUpdates from '../services/unsubscribeFromPresenceUpdates';
import InstantMessageOperationError from '../store/schema/InstantMessageOperationError';
import { getVisibleChats } from '../chatManager/chatFilters';

const DEFAULT_TIME_TO_WAIT_TO_RETRY = 5000;

let toBeSubscribedList: string[] = [];
let activeSubscriptionsList: string[] = [];
let toBeUnsubscribedList: string[] = [];
let unsubscribeActionInProgress: boolean = false;

export function addPresenceSubscription(sipUris: string[]) {
    if (!isSignedIn()) {
        return;
    }

    if (!sipUris || sipUris.length == 0) {
        return;
    }

    let filteredSipUris: string[] = [];
    filterOutActiveChatRecipients(sipUris).forEach(newSipUri => {
        // If this sipUri is in the toBeUnsubscribedList, then remove it from the toBeUnsubscribedList.
        if (toBeUnsubscribedList.includes(newSipUri)) {
            toBeUnsubscribedList.splice(toBeUnsubscribedList.indexOf(newSipUri), 1);
            return;
        }

        // If the subscription for this sipUri is already existed, this is a no-op.
        if (activeSubscriptionsList.includes(newSipUri)) {
            return;
        }

        // If a request has already been issued for this sipUri, then skip requesting again.
        if (toBeSubscribedList.includes(newSipUri)) {
            return;
        }

        // If the same recipient is added multiple times, let's not request subscriptions that many times for same user.
        if (filteredSipUris.includes(newSipUri)) {
            return;
        }

        filteredSipUris.push(newSipUri);
    });

    if (filteredSipUris.length == 0) {
        return;
    }

    toBeSubscribedList.push(...filteredSipUris);
    subscribeForPresenceUpdates(filteredSipUris)
        .then(result => {
            if (result == InstantMessageOperationError.Success) {
                onSubscribeSucceeded(filteredSipUris);
            } else {
                onSubscribeFailed(filteredSipUris);
            }
        })
        .catch(() => {
            onSubscribeFailed(filteredSipUris);
        });
}

export function removePresenceSubscription(sipUris: string[]) {
    if (!isSignedIn()) {
        return;
    }

    if (!sipUris || sipUris.length == 0) {
        return;
    }

    filterOutActiveChatRecipients(sipUris).forEach(newSipUri => {
        // If we were to create an active subscription for this - let's remove it.
        if (toBeSubscribedList.includes(newSipUri)) {
            toBeSubscribedList.splice(toBeSubscribedList.indexOf(newSipUri), 1);
            return;
        }

        // If this sipUri is already tracked for removal of subscription - let's skip it.
        if (toBeUnsubscribedList.includes(newSipUri)) {
            return;
        }

        // If this sipUri is not in the active subscription list, there is no need to
        // unsubscribe this sipUri.
        if (!activeSubscriptionsList.includes(newSipUri)) {
            return;
        }

        toBeUnsubscribedList.push(newSipUri);
    });

    startUnsubscribeProcess();
}

function onSubscribeSucceeded(filteredSipUris: string[]) {
    filteredSipUris.forEach(sipUri => {
        if (!activeSubscriptionsList.includes(sipUri)) {
            activeSubscriptionsList.push(sipUri);
        }

        if (toBeSubscribedList.includes(sipUri)) {
            toBeSubscribedList.splice(toBeSubscribedList.indexOf(sipUri), 1);
        }
    });
}

function onSubscribeFailed(filteredSipUris: string[]) {
    filteredSipUris.forEach(sipUri => {
        if (toBeSubscribedList.includes(sipUri)) {
            toBeSubscribedList.splice(toBeSubscribedList.indexOf(sipUri), 1);
        }
    });
}

function startUnsubscribeProcess() {
    if (toBeUnsubscribedList.length == 0) {
        return;
    }

    if (unsubscribeActionInProgress) {
        return;
    }

    unsubscribeActionInProgress = true;
    const sipUri = toBeUnsubscribedList[0];

    // If the sipUri is not in the active subscription list,
    // then remove it and try to unsubscribe process again.
    if (!activeSubscriptionsList.includes(sipUri)) {
        toBeUnsubscribedList.splice(toBeUnsubscribedList.indexOf(sipUri), 1);
        unsubscribeActionInProgress = false;
        startUnsubscribeProcess();

        return;
    }

    unsubscribeFromPresenceUpdates(sipUri)
        .then(result => {
            if (result == InstantMessageOperationError.Success) {
                onUnsubscribeSucceeded(sipUri);
            } else {
                onUnsubscribeFailed();
            }
        })
        .catch(() => {
            onUnsubscribeFailed();
        });
}

function onUnsubscribeSucceeded(sipUri: string) {
    unsubscribeActionInProgress = false;

    if (toBeUnsubscribedList.includes(sipUri)) {
        toBeUnsubscribedList.splice(toBeUnsubscribedList.indexOf(sipUri), 1);
    }

    if (activeSubscriptionsList.includes(sipUri)) {
        activeSubscriptionsList.splice(activeSubscriptionsList.indexOf(sipUri), 1);
    }

    startUnsubscribeProcess();
}

function onUnsubscribeFailed() {
    unsubscribeActionInProgress = false;

    // Wait for a while and try the unsubscribe process again.
    window.setTimeout(() => {
        startUnsubscribeProcess();
    }, DEFAULT_TIME_TO_WAIT_TO_RETRY);
}

function filterOutActiveChatRecipients(sipUris: string[]): string[] {
    let filteredUris = [...sipUris];

    getVisibleChats().forEach(chat => {
        chat.participants.forEach(participant => {
            const index = filteredUris.indexOf(participant);
            if (index >= 0) {
                filteredUris.splice(index, 1);
            }
        });
    });

    return filteredUris;
}
